import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { usePartnerViewed } from "@/utils/trackingHooks";
import { ArrowRight, TrendingUp, Gift, Award, HeartHandshake, Users, ShoppingCart, CheckCircle, Leaf, Phone } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import LeadForm from "@/forms/engine/LeadForm";
import partnerEnquiry from "@/forms/definitions/partnerEnquiry";
import PaymentMethods from "@/components/PaymentMethods";
import SnowballGraph from "@/components/SnowballGraph";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import RelatedReviews from "@/components/RelatedReviews";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import { useEcologi } from "@/components/EcologiBadge";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const viewportOnce = { once: true, amount: 0.05 };
const heroVisualInitial = { opacity: 0, y: 20 };
const heroVisualAnimate = { opacity: 1, y: 0 };
const heroVisualTransition = { duration: 0.5, delay: 0.3 };

const WHO_ITS_FOR = [
  { title: "Accountants & Bookkeepers", desc: "You already review your clients' payment statements. Now you can save them money — and earn from it." },
  { title: "Web Developers & Agencies", desc: "Building e-commerce sites? Recommend CanDo as the payment provider and earn recurring commission on every site you build." },
  { title: "Business Consultants", desc: "If your clients take card payments, you're sitting on a referral opportunity. No sales work required — just an introduction." },
  { title: "EPOS Resellers", desc: "Already selling EPOS? Add CanDo Payments to your stack and earn on processing volume as well as hardware." },
  { title: "IT Support Companies", desc: "You set up the tech. We provide the payment processing. Your clients get a seamless experience — and you earn monthly." },
  { title: "Anyone Who Knows Businesses", desc: "Tradesperson? Networking group member? Small business owner yourself? If you know people who take card payments, we want to talk." },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Introduce us", desc: "Simply put us in touch with a business that takes — or wants to take — card payments. No hard selling required." },
  { step: "02", title: "We do the work", desc: "Our team handles everything: the review, the quote, the setup, the migration. You don't need to be a payments expert." },
  { step: "03", title: "You earn. Every month.", desc: "Once the merchant is live, you earn recurring commission for as long as they process with us. It compounds." },
];

const PARTNER_FAQS = [
  { q: "Do I need technical knowledge?", a: "Not at all. You just make an introduction — we handle the entire sales process, setup, and onboarding. You don't need to know anything about payments." },
  { q: "How do I get paid?", a: "Commission is paid directly into your bank account every month. You'll have a partner dashboard where you can track all your referrals and earnings in real time." },
  { q: "What support do I get?", a: "Every partner gets a dedicated account manager — a real person who knows your name and is available by phone, email, or WhatsApp. We also provide marketing materials and co-branded collateral." },
  { q: "Is there a minimum number of referrals?", a: "No. Whether you refer one business or fifty, you'll earn commission on every single one. There are no targets and no pressure." },
  { q: "What if my referral doesn't switch?", a: "No problem. There's no obligation on anyone. We'll review their current setup, show them what they could save, and if they decide to stay where they are — no hard feelings. You still keep your existing commission on other referrals." },
  { q: "How long does it take to get started?", a: "Usually within 24–48 hours. Fill in the form, we'll call you, set you up on the partner portal, and you can start referring immediately." },
];

function PartnerFAQ() {
  return <ResponsiveAccordion items={PARTNER_FAQS} testIdPrefix="partner-faq" />;
}

export default function PartnerProgrammePage() {
  usePartnerViewed("partner_programme", { itemType: "partner_programme" });
  const eco = useEcologi();

  return (
    <div>
      <SEOHead title="Merchant Services Partner Programme" description="Join the CanDo Payments partner programme today. Collaborate with us to offer industry-leading merchant services and earn generous ongoing commissions." path="/partner-programme" />

      {/* Hero — 2 column */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Partner Programme</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Refer businesses. <span className="gradient-text">Earn every month.</span></motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-4">Know a business that takes card payments? Introduce them to CanDo and earn recurring commission — every single month they process with us. No sales experience needed. No targets. Just genuine referrals.</motion.p>
              <motion.div variants={fadeUp} className="flex items-center gap-4 text-sm text-brand-black font-semibold mb-8">
                <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> £100–£300/month per referral</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Monthly iPad draw</span>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#become-partner" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine">Become a Partner <ArrowRight size={16} /></a>
                <Link to="/epos-partner-calculator" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors">Calculate My Earnings</Link>
              </motion.div>
            </motion.div>
            <motion.div initial={heroVisualInitial} animate={heroVisualAnimate} transition={heroVisualTransition} className="hidden lg:block">              <div className="bg-white rounded-2xl shadow-xl border border-brand-light-grey/60 p-6 max-w-sm mx-auto">
                <p className="text-[10px] uppercase tracking-widest text-brand-grey mb-1">Partner earnings example</p>
                <p className="text-4xl font-heading font-bold text-brand-aqua-dark">£2,400<span className="text-lg text-brand-grey">/yr</span></p>
                <p className="text-xs text-brand-green font-semibold mt-1">From just 10 referrals</p>
                <div className="mt-5 space-y-3">
                  {[
                    { referrals: "5", monthly: "£100", annual: "£1,200" },
                    { referrals: "10", monthly: "£200", annual: "£2,400" },
                    { referrals: "25", monthly: "£500", annual: "£6,000" },
                    { referrals: "50+", monthly: "£1,000+", annual: "£12,000+" },
                  ].map((row) => (
                    <div key={row.referrals} className="flex items-center justify-between text-sm border-t border-brand-light-grey/60 pt-2">
                      <span className="text-brand-grey">{`${row.referrals} referrals`}</span>
                      <span className="font-bold text-brand-black">{row.annual}<span className="text-xs text-brand-grey font-normal">/yr</span></span>
                    </div>
                  ))}
                </div>
                <Link to="/epos-partner-calculator" className="block text-center mt-4 text-xs font-semibold text-brand-aqua hover:underline">
                  Calculate your exact earnings →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-4 bg-brand-aqua-light border-y border-brand-aqua/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: TrendingUp, text: "Recurring commission" },
            { icon: HeartHandshake, text: "Dedicated account manager" },
            { icon: Gift, text: "Monthly iPad draw" },
            { icon: Leaf, text: "Tree planted per referral" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2">
              <item.icon size={16} className="text-brand-aqua flex-shrink-0" />
              <span className="text-sm font-semibold text-brand-black">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Three steps. Zero hassle.</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-brand-aqua text-white font-heading text-2xl font-bold flex items-center justify-center mx-auto mb-4">{item.step}</div>
                <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Partner Benefits</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Why partners choose CanDo</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: TrendingUp, title: "Recurring Commission", desc: "Not a one-off bonus. You earn a share of revenue for as long as your referral processes with us. It grows with them." },
              { icon: Award, title: "Tiered Rates", desc: "The more businesses you refer, the higher your commission rate. Every tier is a pay rise — permanently." },
              { icon: Gift, title: "Monthly iPad Draw", desc: "Every new partner is entered into our monthly iPad giveaway. No catch. Just our way of saying thanks." },
              { icon: HeartHandshake, title: "Your Own Account Manager", desc: "A real person who knows your name, answers your calls, and makes sure your clients are looked after." },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <item.icon size={24} className="text-brand-aqua mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Who It&apos;s For</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">If you know businesses, this is for you</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHO_ITS_FOR.map((item) => (
              <div key={item.title} className="bg-gray-50 border border-brand-light-grey rounded-xl p-5 hover:bg-white hover:shadow-sm transition-all">
                <h3 className="font-heading font-bold text-brand-black text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator CTAs */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">See what you could earn</h2>
            <p className="text-sm text-brand-grey mt-2">Use our free calculators to estimate your potential recurring income.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Link to="/epos-partner-calculator" className="group block bg-white border border-brand-light-grey rounded-2xl p-6 hover:border-brand-aqua hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid="partner-calc-epos">
              <div className="w-12 h-12 rounded-xl bg-brand-aqua flex items-center justify-center mb-4"><Users size={22} className="text-white" /></div>
              <h3 className="font-heading text-lg font-bold text-brand-black mb-2">EPOS Partner Calculator</h3>
              <p className="text-sm text-brand-grey leading-relaxed mb-3">Estimate recurring income from referring retail and hospitality merchants.</p>
              <span className="text-sm font-semibold text-brand-aqua flex items-center gap-1 group-hover:gap-2.5 transition-all">Calculate my earnings <ArrowRight size={14} /></span>
            </Link>
            <Link to="/ecom-partner-calculator" className="group block bg-white border border-brand-light-grey rounded-2xl p-6 hover:border-brand-aqua hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid="partner-calc-ecom">
              <div className="w-12 h-12 rounded-xl bg-brand-aqua flex items-center justify-center mb-4"><ShoppingCart size={22} className="text-white" /></div>
              <h3 className="font-heading text-lg font-bold text-brand-black mb-2">E-Commerce Partner Calculator</h3>
              <p className="text-sm text-brand-grey leading-relaxed mb-3">Estimate recurring income from referring online merchants.</p>
              <span className="text-sm font-semibold text-brand-aqua flex items-center gap-1 group-hover:gap-2.5 transition-all">Calculate my earnings <ArrowRight size={14} /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Snowball Growth Graph */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">The Snowball Effect</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">Watch your income grow month by month</h2>
            <p className="text-sm text-brand-grey max-w-xl mx-auto mt-2">Drag the slider to see how referral income compounds over 12 months. The more you refer, the faster the snowball grows.</p>
          </div>
          <SnowballGraph />
        </div>
      </section>

      {/* FAQ / Objection Handling */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Common Questions</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">Everything you need to know</h2>
          </div>
          <PartnerFAQ />
        </div>
      </section>

      {/* Trust & Credibility */}
      <section className="py-12 bg-gray-50 border-y border-brand-light-grey/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <PaymentMethods size="md" />
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-brand-grey">
            <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Powered by Elavon</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> FCA Regulated</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Better Business Act Supporter</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Certified B Corporation</span>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-10 bg-brand-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { value: "100s", label: "Merchants Live" },
            { value: `${eco.trees > 0 ? eco.trees.toLocaleString() : "..."}`, label: "Trees Planted" },
            { value: "Elavon", label: "Payment Partner" },
            { value: "<4 min", label: "Support Response" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-2xl sm:text-3xl font-heading font-bold text-brand-aqua">{s.value}</p>
              <p className="text-xs text-brand-grey uppercase tracking-wider mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Become a Partner form */}
      <section id="become-partner" className="py-16 md:py-20 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Get Started</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Ready to start earning?</h2>
            <p className="text-brand-grey leading-relaxed mb-6">Fill in the form and we&apos;ll get back to you within 24 hours with everything you need to start referring. No contracts, no commitments — just a conversation about how we can work together.</p>
            <div className="space-y-3 mb-8">
              {[
                "No minimum referrals required",
                "Commission paid monthly into your bank account",
                "Full dashboard to track your referrals and earnings",
                "Marketing materials provided",
                "Dedicated partner manager from day one",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 bg-gray-50 border border-brand-light-grey rounded-xl p-4">
              <Phone size={18} className="text-brand-aqua flex-shrink-0" />
              <div>
                <p className="text-xs text-brand-grey">Prefer to talk? Call our partner team:</p>
                <a href="tel:+448003682989" className="text-sm font-bold text-brand-black hover:text-brand-aqua transition-colors">0800 368 2989</a>
              </div>
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Become a partner</h3>
              <p className="text-sm text-brand-grey mb-4">Tell us a bit about yourself and we&apos;ll take it from there.</p>
              <LeadForm schema={partnerEnquiry} context={{ page: "Partner Programme" }} />
            </div>
            <FormReviewCompanion industry="partnership" product="partners" label="What our partners say" testId="partner-programme-form-review" />
          </div>
        </div>
      </section>

      <RelatedReviews
        industry="partnership"
        product="partners"
        eyebrow="Partner Stories"
        heading="See what our partners say about working with us"
        subheading="Real feedback from agencies, developers and referral partners earning alongside CanDo."
        testId="partner-related-reviews"
      />
    </div>
  );
}
